import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ITCOFieldValues,
  ITCOSubmission,
  ITCOField,
  ITCOFieldInput,
} from 'types';
import {
  useAnalytics,
  useQueryData,
  useUpdateTcoSubmissions,
  useDeleteTcoSubmissions,
  useGetTcoLocations,
} from 'hooks';
import { useDebouncedCallback } from 'use-debounce/lib';
import { useParams } from 'react-router-dom';
import { collatorSort } from 'utils';
import { TCOBasicInput, TCOSelect, TCOCustomInput } from 'components';
import {
  resetAverageOverTerm,
  resetDeviceCalculation,
  tcoFieldsIds,
  tcoGeneralInformationFieldIds,
} from 'utils/defaultsAndConstants/constants';
import { useRefreshTcoDeviceSelections } from './useRefreshTcoDeviceSelections';

interface IUseRenderFieldsParams {
  fields: ITCOField[];
  tcoSubmissions: ITCOSubmission[];
  formId: string;
}

export const useTCORenderFields = ({
  fields,
  tcoSubmissions,
  formId,
}: IUseRenderFieldsParams) => {
  const { estimateId = '' } = useParams();
  const { userId } = useQueryData();
  const { trackSelectAction } = useAnalytics();

  const { termsFieldId } = tcoFieldsIds;

  const {
    operatingLabourCostPerHourId,
    annualLaborInflationRateId,
    averageTermCostId,
  } = tcoGeneralInformationFieldIds;

  const { refreshDeviceSelections } = useRefreshTcoDeviceSelections(
    estimateId || ''
  );

  const { data: tcoLocations = [] } = useGetTcoLocations({
    params: { estimateId },
  });

  const initFieldValues = useMemo(() => {
    const baseValues = fields.reduce((acc: ITCOFieldValues, field) => {
      const submission = tcoSubmissions?.find(
        (sub) => sub.fieldId === field.id
      )?.answer;
      acc[field.id] = submission || null;
      return acc;
    }, {} as ITCOFieldValues);
    return baseValues;
  }, [fields, tcoSubmissions]);

  /* * * * * * * *
   *             *
   * STATE/HOOKS *
   *             *
   * * * * * * * */

  const [fieldValues, setFieldValues] = useState(initFieldValues);
  const [currentFocusId, setCurrentFocusId] = useState<string>('');

  const { tcoDeviceSelections } = useQueryData();

  /* * * * * *
   *         *
   * HELPERS *
   *         *
   * * * * * */

  const checkIsDirty = useCallback(
    (fieldValues: ITCOFieldValues) =>
      Object.entries(initFieldValues).some(
        ([fieldId, value]) => fieldValues[fieldId] !== value
      ),
    [initFieldValues]
  );
  const getFieldsToUpdate = useCallback(
    (fieldValues: ITCOFieldValues) => {
      const isDirty = checkIsDirty(fieldValues);
      if (!isDirty) {
        return [];
      }
      return Object.entries(fieldValues).filter(
        ([fieldId, value]) => initFieldValues[fieldId] !== value
      );
    },
    [checkIsDirty, initFieldValues]
  );

  const { updateTcoSubmissions, updateTcoSubmissionsError } =
    useUpdateTcoSubmissions({ estimateId });

  const { deleteTcoSubmissions } = useDeleteTcoSubmissions({ estimateId });

  const saveSubmissions = useCallback(
    (tcoSubmissions: Partial<ITCOSubmission>[]) => {
      if (!tcoSubmissions.length) return;
      updateTcoSubmissions(tcoSubmissions);
    },
    [updateTcoSubmissions]
  );
  const sortedFields = useMemo(() => {
    return fields.sort((a, b) => a.order - b.order);
  }, [fields]);

  /* * * * * * * * * *
   *                 *
   * INTERACTION HANDLERS  *
   *                 *
   * * * * * * * * * */

  const handleFocus = useCallback(
    (fieldId: string) => () => {
      setCurrentFocusId(fieldId);
    },
    []
  );

  const findSubmissionDuplicates = useCallback(
    (config: {
      tcoSubmissions: ITCOSubmission[];
      fieldValues: ITCOFieldValues;
    }) => {
      const { tcoSubmissions, fieldValues } = config;
      const fieldEntries = Object.entries(fieldValues);
      const submissionsToDelete = fieldEntries.reduce(
        (acc: ITCOSubmission[], [fieldId]) => {
          const subMatches = tcoSubmissions
            .filter((sub) => sub.fieldId === fieldId)
            .sort((a, b) => collatorSort(a.createdAt, b.createdAt));
          const mostRecentSub = subMatches.at(-1);
          acc.push(...subMatches.filter((s) => s.id !== mostRecentSub?.id));
          return acc;
        },
        []
      );
      return submissionsToDelete;
    },
    []
  );

  const resetAverageCostValue = useCallback(
    (config: { tcoSubmissions: ITCOSubmission[] }) => {
      const { tcoSubmissions } = config;
      var operatingCost = Number(
        tcoSubmissions.find((s) => s.fieldId === operatingLabourCostPerHourId)
          ?.answer
      );

      var inflation = Number(
        tcoSubmissions.find((s) => s.fieldId === annualLaborInflationRateId)
          ?.answer
      );

      var term = Number(
        tcoSubmissions.find((s) => s.fieldId === termsFieldId)?.answer
      );

      let total = 0;
      let current = operatingCost;
      for (let i = 1; i <= term; i++) {
        total += current;
        current *= 1 + inflation / 100;
      }
      const averageCostValue = (total / term).toFixed(2);
      const averageTermCostSubmission = tcoSubmissions.filter(
        (submission) => submission.fieldId === averageTermCostId
      )[0];

      return {
        ...averageTermCostSubmission,
        answer: averageCostValue,
      };
    },
    [
      operatingLabourCostPerHourId,
      annualLaborInflationRateId,
      termsFieldId,
      averageTermCostId,
    ]
  );

  const onFieldChange = useCallback(
    (id: string, newValue: ITCOSubmission['answer'], callback?: () => void) => {
      const newFieldValues = { ...fieldValues, [id]: newValue };

      const fieldsToUpdate = getFieldsToUpdate(newFieldValues);
      const submissionsToSave = fieldsToUpdate.reduce(
        (acc: Partial<ITCOSubmission>[], [fieldId]) => {
          const subMatches = tcoSubmissions
            .filter((sub) => sub.fieldId === fieldId)
            .sort((a, b) => collatorSort(a.createdAt, b.createdAt));
          const mostRecentSub = subMatches.at(-1);
          if (mostRecentSub) {
            const newSub: Partial<ITCOSubmission> = {
              ...mostRecentSub,
              formId: undefined,
              answer: newFieldValues[fieldId] ?? null,
              updatedBy: userId,
            };
            acc.push(newSub);
          } else {
            acc.push({
              estimateId,
              fieldId,
              answer: newFieldValues[fieldId] ?? null,
              updatedBy: userId,
            });
          }
          return acc;
        },
        []
      );

      const latestSubmission = submissionsToSave[0];
      let updatedLatestSubmissions = [...(tcoSubmissions || [])].map(
        (submission) => {
          if (latestSubmission?.fieldId === submission.fieldId) {
            return {
              ...submission,
              answer: latestSubmission?.answer || '',
            };
          } else {
            return submission;
          }
        }
      );
      if (
        submissionsToSave.find((s) =>
          resetDeviceCalculation?.includes(s.fieldId!)
        )
      ) {
        refreshDeviceSelections({
          latestSubmission,
          tcoSubmissions: updatedLatestSubmissions,
          tcoDeviceSelections,
          tcoLocations,
        });
      }

      if (
        submissionsToSave.find((s) =>
          resetAverageOverTerm?.includes(s.fieldId!)
        )
      ) {
        submissionsToSave.push(
          resetAverageCostValue({
            tcoSubmissions: updatedLatestSubmissions,
          })
        );
      }

      saveSubmissions(submissionsToSave);

      if (callback) {
        callback();
      }
      trackSelectAction(
        `Tco Submission Change (Field Id: ${id}, Answer: ${newValue})`,
        { opType: 'inputFieldUpdate' }
      );
    },
    [
      fieldValues,
      getFieldsToUpdate,
      saveSubmissions,
      trackSelectAction,
      tcoSubmissions,
      userId,
      estimateId,
      tcoDeviceSelections,
      tcoLocations,
      refreshDeviceSelections,
      resetAverageCostValue,
    ]
  );

  const debounceFieldChange = useDebouncedCallback(onFieldChange, 350);

  /* * * * * * * * * *
   *                 *
   * RENDER METHODS  *
   *                 *
   * * * * * * * * * */

  const renderLabel = useCallback((field: ITCOFieldInput) => {
    const { label } = field;
    return (
      <div className="tco-field__label">
        <div className="tco-field__label-question">
          <span dangerouslySetInnerHTML={{ __html: label }}></span>
        </div>
      </div>
    );
  }, []);

  const renderInput = useCallback(
    (field: ITCOFieldInput) => {
      switch (field.inputType) {
        case 'select': {
          return (
            <TCOSelect fieldInput={field} onFieldChange={debounceFieldChange} />
          );
        }
        case 'text':
          return (
            <TCOBasicInput
              fieldInput={field}
              onFieldChange={debounceFieldChange}
              inputType="text"
            />
          );
        case 'number-text':
          return (
            <TCOBasicInput
              fieldInput={field}
              onFieldChange={debounceFieldChange}
              inputType="number"
            />
          );
        case 'number-percent':
          return (
            <TCOCustomInput
              fieldInput={field}
              onFieldChange={debounceFieldChange}
              inputType="percent"
            />
          );
        case 'number-dollar':
          return (
            <TCOCustomInput
              fieldInput={field}
              onFieldChange={debounceFieldChange}
              inputType="dollar"
            />
          );
        default:
          return null;
      }
    },
    [debounceFieldChange]
  );

  const fieldComponents = useMemo(() => {
    return sortedFields.map((field) => {
      const { id, inputType } = field;
      const value = fieldValues[id];
      const fieldInput = { ...field, value };
      return (
        <div
          key={id}
          onFocus={handleFocus(id)}
          onBlur={handleFocus('')}
          className="tco-field"
          style={{
            display: 'grid',
            gridTemplateRows: 'auto auto',
            gridColumn: inputType === 'text-quantity' ? 'span 2' : '',
          }}
        >
          <div className="tco-field-input">
            {renderLabel(fieldInput)}
            {renderInput(fieldInput)}
          </div>
        </div>
      );
    });
  }, [sortedFields, fieldValues, renderLabel, renderInput, handleFocus]);

  /* * * * * * *
   *           *
   *  EFFECTS  *
   *           *
   * * * * * * */

  useEffect(() => {
    if (!debounceFieldChange.isPending() && checkIsDirty(fieldValues)) {
      const subsToDelete = findSubmissionDuplicates({
        tcoSubmissions,
        fieldValues: initFieldValues,
      });
      if (subsToDelete.length) {
        deleteTcoSubmissions(subsToDelete.map((s) => s.id));
      }
      setFieldValues(() => initFieldValues);
    }
  }, [
    checkIsDirty,
    debounceFieldChange,
    deleteTcoSubmissions,
    fieldValues,
    findSubmissionDuplicates,
    initFieldValues,
    tcoSubmissions,
  ]);

  return {
    currentFocusId,
    fieldComponents,
    fieldValues,
    fieldsToUpdate: getFieldsToUpdate(fieldValues),
    isDirty: checkIsDirty(fieldValues),
    updateTcoSubmissionsError,
  };
};
